import {
	ApiEjariProperty,
	BaseEjariContract,
	EjariContractNormalisedStatus,
	EjariContractStatus,
	EjariProperty,
	EjariPropertyType,
	EjariResponse,
	ErrorData,
	LeaseResponse,
	Lookup,
	LookupLike,
	ManagementContract,
	ValidatePropertyRequest
} from "./Entity";
import Hooks from "./Hooks";
import { LocaleValue } from "@eres/core/dist/LocaleValue";
import { LocaleUtil, StringUtil } from "@eres/core";
import { TabuPropertyType } from "@my-dld/utils/PropertyUtils";
import { getErrorLookups } from "./api";
import { LookupTransformer } from "./IApi";
import { EjariUserType } from "@eres/ejari-core/reducers/EjariData";
import { TenancyContactAction, TenantRequestStatus } from "@eres/ejari-core/constants";
import { EjariApi } from "./index";
import { EjariLookUp } from "./constants";
import _ from "lodash";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { PropertyInfo } from "./Entity/tenant";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { AssociatedDetailedProperty } from "./Entity/management_contract";

export function processLeaseResponse( response: any ) {
	let contracts = response.Response;
	if ( contracts && contracts.length && contracts.length > 0 ) {
		for ( const c of contracts ) {
			if ( Hooks.propertyProcessors ) {
				const ids = Object.keys( Hooks.propertyProcessors ).sort();
				for ( const id of ids ) {
					const processor = Hooks.propertyProcessors[ id ];
					c.Properties = c.Properties.map( ( p: any ) => processor( p ) );
				}
			}

		}
	}
	if ( contracts == null ) contracts = [];
	const nr: LeaseResponse = {
		Leases: contracts.filter( ( x: { IsSublease: number | undefined; } ) => ( x.IsSublease == null || x.IsSublease === 0 ) ),
		rawSubLease: contracts.filter( ( x: { IsSublease: number | undefined; } ) => ( x.IsSublease !== undefined && x.IsSublease === 1 ) ),
		SubLeases: [],
	};
	if ( nr.rawSubLease && nr.rawSubLease.length > 0 ) {
		// nr.SubLeases = groupSubLease(nr.rawSubLease);
	}
	return nr;
}

export function IDtoEnum<T>( enumObject: any, id: any ): T | undefined {
	if ( enumObject[ id ] )
		return enumObject[ enumObject[ id ] ] as any as T;
	return undefined;
}

interface NormalizeSink {
	Properties: EjariProperty[];
	ContractStatusId?: number;
	ContractNumber?: string;
	ContractStatusNameEn?: string;
	ContractStatusNameAr?: string;
}

type KeysMatching<T, V> = { [K in keyof T] -?: T[K] extends V ? K : never }[keyof T];
type StringKeys<X> = KeysMatching<X, string | undefined>;
type EjariApiStingKeys = StringKeys<EjariProperty>;

function convertToLocaleValue<S extends Object, K extends StringKeys<S>>( subject: S, enKey: K, arKey: K ): LocaleValue | undefined {
	const x = subject[ enKey ];
	let enValue: string | undefined = subject[ enKey ] as any as string;
	let arValue: string | undefined = subject[ arKey ] as any as string;
	if ( StringUtil.isEmptyStringOrNull( enValue ) )
		enValue = undefined;
	if ( StringUtil.isEmptyStringOrNull( arValue ) )
		arValue = undefined;
	if ( enValue != null || arValue != null ) {
		return {
			en: enValue,
			ar: arValue,
		};
	}
	return undefined;
}


export function EjariPropertyFromTenantRequest( input: PropertyInfo ): EjariProperty {
	const p: Partial<EjariProperty> = {
		...input,
	};
	if ( input.PropertyTypeId != null )
		p.PropertyType = IDtoEnum<EjariPropertyType>( EjariPropertyType, p.PropertyTypeId )!;
	if ( p.PropertyType === EjariPropertyType.UNIT && input.Unit ) {
		p.BuildingName = convertToLocaleValue( input.Unit, "BuildingNameEn", "BuildingNameAr" );
		p.UnitNo = input.Unit.UnitNumber;
		p.Size = "" + input.Unit.ActualArea;
	} else if ( p.PropertyType === EjariPropertyType.VILLA && input.Villa ) {
		p.BuildingName = convertToLocaleValue( input.Villa, "VillaName", "VillaArabicName" );
		p.Size = "" + input.Villa.ActualArea;
	}
	p.Area = convertToLocaleValue( input, "AreaNameEn", "AreaNameAr" );
	p.LandDMNumber = input.DubaiMunicipalityNumber;
	p.Usage = convertToLocaleValue( input, "UsageNameEN", "UsageNameAr" );
	p.EjariType = convertToLocaleValue( input, "PropertyTypeNameEn", "PropertyTypeNameAr" );
	p.EjariSubType = convertToLocaleValue( input, "PropertySubtypeNameEn", "PropertySubtypeNameAr" );
	p.EjariPropertyUsageId = input.PropertyUsageId;
	return p as EjariProperty;
}

export function EjariPropertyFromApi( input: ApiEjariProperty ): EjariProperty {
	const p: Partial<EjariProperty> = {
		...input,
	};
	if ( input.PropertyTypeId != null )
		p.PropertyType = IDtoEnum<EjariPropertyType>( EjariPropertyType, p.PropertyTypeId )!;
	p.TenantName = convertToLocaleValue( input, "TenantNameEn", "TenantNameEn" );
	p.BuildingName = convertToLocaleValue( input, "BuildingNameEn", "BuildingNameAr" );
	p.Area = convertToLocaleValue( input, "AreaEn", "AreaAr" );
	p.LandType = convertToLocaleValue( input, "LandTypeNameEn", "LandTypeNameAr" );
	p.Usage = convertToLocaleValue( input, "UsageNameEn", "UsageNameAr" );
	p.EjariUsage = convertToLocaleValue( input, "EjariUsageNameEn", "EjariUsageNameAr" );
	p.EjariType = convertToLocaleValue( input, "EjariTypeNameEn", "EjariTypeNameAr" );
	p.EjariSubType = convertToLocaleValue( input, "EjariSubTypeNameEn", "EjariSubTypeNameAr" );
	p.LandType = convertToLocaleValue( input, "LandTypeNameEn", "LandTypeNameAr" );

	p.EjariPropertyId = normalizedPropertyId( input );
	p.LandNumber = input.LandNo;
	if ( !StringUtil.isEmptyStringOrNull( input.LandSubNo ) )
		p.LandNumber = p.LandNumber + "-" + input.LandSubNo;
	p.LandDMNumber = input.LandDmNo;
	if ( !StringUtil.isEmptyStringOrNull( input.LandDmSubNo ) )
		p.LandDMNumber = p.LandDMNumber + "-" + input.LandDmSubNo;
	if ( !StringUtil.isEmptyStringOrNull( input.Latitude ) && !StringUtil.isEmptyStringOrNull( input.Longitude ) ) {
		p.Location = {
			Latitude: Number( input.Latitude ),
			Longitude: Number( input.Longitude )
		};
	}
	p.hasMore = input.MoreData === "True";
	if ( StringUtil.isEmptyStringOrNull( p.UnitNo ) ) {
		p.UnitNo = p.PropertyNumber;
	}
	let owners = p.Owners;
	if ( owners && owners.length > 0 ) {
		owners = owners.map( value => {
			const own = {
				...value,
			};
			own.OwnerName = convertToLocaleValue( own, "OwnerNameEn", "OwnerNameAr" );
			return own;
		} );
		p.Owners = owners;
	}
	let contracts: BaseEjariContract[] | undefined = p.AssociatedContracts;
	if ( contracts && contracts.length > 0 ) {
		contracts = contracts.map( value => {
			return {
				...value,
				ContractStatus: getContractStatus( value ),
				ContractStatusName: convertToLocaleValue( value, "ContractStatusNameEn", "ContractStatusNameAr" ),
			};
		} );
		p.AssociatedContracts = contracts;
	}
	let mc: ManagementContract | undefined = p.AssociatedManagementContract;
	if ( mc ) {
		mc = {
			...mc,
			CompanyName: convertToLocaleValue( mc, "CompanyNameEn", "CompanyNameAr" ),
			Status: convertToLocaleValue( mc, "McStatusNameEn", "McStatusNameAr" ),
			ContractType: convertToLocaleValue( mc, "McTypeNameEn", "McTypeNameAr" ),
		};
		p.AssociatedManagementContract = mc;
	}
	return p as EjariProperty;
}

export function EjariPropertiesFromApi( apiProperties: ApiEjariProperty[] ): EjariProperty[] {
	return apiProperties.map( ( apiProperty: ApiEjariProperty ) => EjariPropertyFromApi( apiProperty ) );
}

export function attachLeasToProperty( property: EjariProperty, propertySink: NormalizeSink ) {
	if ( propertySink ) {

		property.ContractStatus = propertySink.ContractStatusId;
		property.ContractNumber = propertySink.ContractNumber;
		property.ContractStatusName = convertToLocaleValue( propertySink, "ContractStatusNameEn", "ContractStatusNameAr" );
		const { Properties, ...others } = propertySink;
		property.CurrentContract = {
			...others,
		} as BaseEjariContract;
	}
}

export function normalizedPropertyId( property: ApiEjariProperty ) {
	switch ( property.PropertyTypeId ) {
		case EjariPropertyType.BUILDING:
			return property.BuildingPropertyId;
		case EjariPropertyType.COMPOUND:
		case EjariPropertyType.LAND:
			return property.LandPropertyId;
		case EjariPropertyType.UNIT:
			return property.UnitPropertyId;
		case EjariPropertyType.VILLA:
		case EjariPropertyType.VIRTUAL_VILLA:
			return property.VillaPropertyId;
		default:
			return property.LandPropertyId;
	}
}

export function getLastContract( ejariPropDetails: EjariProperty ) {
	if ( ejariPropDetails.AssociatedContracts ) {
		const size = ejariPropDetails.AssociatedContracts.length;
		if ( size > 0 ) {
			return ejariPropDetails.AssociatedContracts[ size - 1 ];
		}
	}
	return undefined;
}

export function getContractStatus( contract: any ): EjariContractStatus {
	if ( contract && contract.ContractStatusId ) {
		return IDtoEnum<EjariContractStatus>( EjariContractStatus, contract.ContractStatusId )!;
	}
	return EjariContractStatus.PENDING;
}

export function getContractNormalisedStatus( contract: any ): EjariContractNormalisedStatus {
	if ( contract && contract.ContractStatusId ) {
		if ( contract.ContractTypeId === 1 ) {
			switch ( contract.ContractStatusId ) {
				case 1:
					return EjariContractNormalisedStatus.PENDING;
				case 2:
					return EjariContractNormalisedStatus.ACTIVE;
				case 3:
					return EjariContractNormalisedStatus.CANCELED;
				case 4:
					return EjariContractNormalisedStatus.RENEWED;
				case 5:
					return EjariContractNormalisedStatus.TERMINATED;
				default:
					return EjariContractNormalisedStatus.PENDING;
			}
		} else {
			switch ( contract.ContractStatusId ) {
				case 1:
					return EjariContractNormalisedStatus.PENDING;
				case 2:
					return EjariContractNormalisedStatus.ACTIVE;
				case 3:
					return EjariContractNormalisedStatus.CANCELED;
				case 11:
					return EjariContractNormalisedStatus.RENEWED;
				case 5:
					return EjariContractNormalisedStatus.UNDER_AUDIT;
				case 6:
					return EjariContractNormalisedStatus.PASS_AUDIT;
				case 9:
					return EjariContractNormalisedStatus.TERMINATED;
				case 10:
					return EjariContractNormalisedStatus.TERMINATION_REQUESTED;
				default:
					return EjariContractNormalisedStatus.PENDING;
			}
		}
	}
	return EjariContractNormalisedStatus.PENDING;
}

export function validateEjariProperty( p: EjariProperty ): Promise<EjariProperty> {
	if ( p.IsFreeHold == null ) {
		return Promise.reject( "Invalid Ejari Property" );
	}
	return Promise.resolve( p );
}

export function getEjariPropertyTypeFromTabu( id: number ) {
	switch ( id ) {
		case TabuPropertyType.VILLA :
			return EjariPropertyType.VILLA;
		case TabuPropertyType.UNIT :
			return EjariPropertyType.UNIT;
		default :
			return 0;
	}
}

export function normalizeNumberForApi( input: any ): any {
	if ( input == null ) return undefined;
	let v = StringUtil.toNumberString( input );
	if ( v && v.endsWith( "." ) ) v = v.substr( 0, v.length );
	return v;
}

export function normalizeEntityNumber<T>( target: T, key: keyof T ) {
	if ( target[ key ] )
		target[ key ] = normalizeNumberForApi( target[ key ] );
}

function doesMatchingCode( actualCode: string, notAnErrorCodes: ( string | number )[] ) {
	for ( const iCode of notAnErrorCodes ) {
		if ( iCode + "" === actualCode + "" ) return true;
	}
	return false;
}


export async function convertErrorIdToMessage( code: number | string | object ): Promise<string> {
	let xm = "";
	if ( typeof code === "object" ) {
		const e = code as any;
		xm = LocaleUtil.getLocalValue( { en: e?.ErrorDescEn, ar: e?.ErrorDescAr } );
	}
	if ( !StringUtil.isEmptyStringOrNull( xm ) ) return xm;
	const lookups = await getErrorLookups();
	if ( lookups && Array.isArray( lookups ) ) {
		const r = lookups.find( lkp => lkp.Id + "" === code + "" );
		if ( r ) {
			return LocaleUtil.getLocalValue( r );
		} else {
			return "Code:" + code;
		}
	}
	return "Code:" + code;
}

function getECode( e: any ) {
	return e.hasOwnProperty( "ErrorEnumCode" ) ? e.ErrorEnumCode : e.Code;
}

export function convertErrorCodesToMessage( errors: ( string | number | object ) [], notAnErrorCodes: ( string | number ) [] = [] ): Promise<string | undefined> {
	if ( Array.isArray( errors ) && errors.length > 0 ) {
		errors = errors.filter( e => {
			const code = typeof e === "object" ? getECode( e ) : e;
			return !doesMatchingCode( code + "", notAnErrorCodes );
		} );
		if ( errors.length > 0 ) {
			return Promise.all( errors.map( e => convertErrorIdToMessage( e ) ) ).then( messages => {
				return Promise.resolve( messages.join( "\n" ) );
			} );
		}
	}
	return Promise.resolve( undefined );
}

export async function ValidateSecondPhase<T>( response: EjariResponse<T>, notErrors: string  [] ) {
	await ValidateSecondPhaseX( response, notErrors );
	await ValidateSecondPhaseX( response.Response, notErrors );
}

export async function ValidateSecondPhaseMgmContract<T extends Record<string, any>>( response: EjariResponse<T>, notErrors: string[] ) {
	await ValidateSecondPhaseX( response, notErrors );
	await ValidateSecondPhaseX( response.Response.Request, notErrors );
}

export async function ValidateSecondPhaseXKey<T>( target: any, notErrors: string  [], key: string ) {
	if ( target && target[ key ] ) {
		let errors = target[ key ]!;
		const message = await convertErrorCodesToMessage( errors, notErrors );
		if ( message ) {
			throw  Error( message );
		}
	}
}

export async function ValidateSecondPhaseX<T>( target: any, notErrors: string  [] ) {
	// ValidationErrors
	await ValidateSecondPhaseXKey( target, notErrors, "ValidationErrors" );
	await ValidateSecondPhaseXKey( target, notErrors, "ContractValidationErrors" );
	// Errors
	if ( target && target.Errors ) {
		let errorsX = target.Errors!;
		if ( typeof errorsX === "number" ) errorsX = [ errorsX ];
		if ( Array.isArray( errorsX ) && errorsX.length > 0 ) {
			const fe = errorsX[ 0 ];
			if ( typeof fe === "number" ) {
				// errors are array of error codes
				let errors = errorsX as number[];
				const message = await convertErrorCodesToMessage( errors, notErrors );
				if ( message ) {
					throw  Error( message );
				}
			} else if ( typeof fe === "object" ) {
				let errors = errorsX as ErrorData[];
				let oErrors = errorsX as ErrorData[];
				const ol = errors.length;
				errors = errors.filter( e => !doesMatchingCode( e.Code + "", notErrors ) );
				// some time error will come with code "0" but there is a valid error message
				if ( errors.length == 0 && ol != 0 ) {
					oErrors = oErrors.filter( e => !doesMatchingCode( e.Code + "", notErrors.filter( e => e + "" !== "0" ) ) );
					if ( oErrors.length > 0 ) {
						let fe = oErrors[ 0 ];
						let m = LocaleUtil.getLocalValue( fe.Message, "en" );
						if ( m != "NoError" ) {
							errors.push( fe );
						}
					}
				}
				if ( errors.length > 0 ) {
					const messages = errors.map( e => e.Message ? LocaleUtil.getLocalValue( e.Message ) : e.Code );
					throw  Error( messages.join( "\n" ) );
				}
			}
		}
	}
}

export async function checkApiRequestResponseError<T>(
	response: EjariResponse<T>,
	notAnErrorCodes: ( string | number ) [] = [],
	skipPhaseTwo = false,
	validation: ( response: EjariResponse<T>, notErrors: string[] ) => Promise<void> = ValidateSecondPhase ): Promise<T> {
	if ( response ) {
		let notErrors = [ "0", ...( notAnErrorCodes.map( x => x + "" ) ) ];
		if ( response.Code != null && doesMatchingCode( response.Code + "", notErrors ) ) {
			if ( !skipPhaseTwo ) await validation( response, notErrors );
			return response.Response;
		} else {
			if ( !skipPhaseTwo ) await validation( response, notErrors );
			if ( response.Message != null ) {
				throw response.Message;
			} else {
				throw Error( "Code " + response.Code );
			}
		}
	} else {
		throw Error( "Empty response from Server" );
	}
}


const MapApiLookUpTransformer: LookupTransformer = ( input: LookupLike ) => {
	return {
		ar: input.NameAr,
		en: input.NameEn,
		Id: input.Identity,
	} as Lookup;
};
const BlockCodeTransformer: LookupTransformer = ( input: LookupLike ) => {
	return {
		ar: input.ArabicText,
		en: input.EnglishText,
		Id: Number( input.BlockCode ),
	} as Lookup;
};
const TerminationReasonTransformer: LookupTransformer = ( input: LookupLike ) => {
	return {
		ar: input.TerminationReasonAr,
		en: input.TerminationReason,
		Id: Number( input.Identity ),
	} as Lookup;
};
export const CountriesTransformer: LookupTransformer = ( input: LookupLike ) => {
	return {
		ar: input.NameAr,
		en: input.NameEn,
		Id: Number( input.Identity ),
		ParentId: Number( input.CountryGroupId ),
	} as Lookup;
};
export const LicenseAuthoritiesEmiratesTransformer: LookupTransformer = ( input: LookupLike ) => {
	let children: Lookup[] = [];
	if ( input && input.AssociatedLicenseSources && Array.isArray( input.AssociatedLicenseSources ) ) {
		children = input.AssociatedLicenseSources.map( x => {
			return {
				ar: x.NameAr,
				en: x.NameEn,
				Id: Number( x.Identity ),
			} as Lookup;
		} );
	}
	return {
		ar: input.NameAr,
		en: input.NameEn,
		Id: Number( input.Identity ),
		Children: children,
	} as Lookup;
};
const TenantDocumentTransformer: LookupTransformer = ( input: LookupLike ) => {
	return {
		ar: input.NameAr,
		en: input.NameEn,
		Id: input.Identity,
		IsMandatory: input.IsMandatory,
		MaxPages: input.MaxPages,
		MaxSizeKb: input.MaxSizeKb,
	} as Lookup;
};
const TermsTransformer: LookupTransformer = ( input: LookupLike ) => {
	return {
		ar: input.DescriptionAr,
		en: input.DescriptionEn,
		Id: input.Identity,
	} as Lookup;
};
const EJLookupTransformer: LookupTransformer = ( input: LookupLike ) => {
	return {
		ar: input.NameAr,
		en: input.NameEn,
		Id: input.Identity,
		ParentId: input.ParentId,
	} as Lookup;
};
const OptionEJLookupTransformer: LookupTransformer = ( input: LookupLike ) => {
	return {
		ar: input.NameAr,
		en: input.NameEn,
		Id: input.Identity,
		ParentId: input.ParentId,
		isOwner: true,
	} as Lookup;
};
export {
	MapApiLookUpTransformer,
	BlockCodeTransformer,
	TerminationReasonTransformer,
	TenantDocumentTransformer,
	TermsTransformer,
	EJLookupTransformer,
	OptionEJLookupTransformer,
};

export function getDisplayableContract( property: EjariProperty, userType: EjariUserType ): BaseEjariContract | undefined {
	let value = getLastContract( property );
	let mc = property.AssociatedManagementContract;
	if ( value && mc ) {
		if ( userType === "owner" && mc.McTypeId != 1 ) {
			value = undefined;
		}
	}

	return value;
}

/*

export function checkApiResponseError<T>(response: EjariResponse<T>): Promise<T> {
    if (response) {
        if (response.Code != undefined && response.Code + "" === "0") {
            return Promise.resolve(response.Response);
        } else {
            if (response.Message != undefined) {
                return Promise.reject(response.Message);
            } else if (response.Errors != undefined && response.Errors.length > 0) {
                return checkValidationErrorWithMessage(response);
            } else {
                return Promise.reject("Error " + response.Code);
            }
        }
    } else {
        return Promise.reject("Empty response from Server");
    }
}
export function checkValidationError(response, key: string = "ValidationErrors", ...skipErrors: number[]): Promise<any> {
    if (response) {
        if (response.hasOwnProperty(key)) {
            let errors = response[key];
            if (errors != undefined) {
                let codes: number[] = [];
                if (Array.isArray(errors)) {
                    if (errors.length === 1 && errors[0] + "" === "0")
                        return response;
                    else if (skipErrors != undefined && skipErrors.length > 0) {
                        errors = errors.filter((v) => {
                            return skipErrors.indexOf(v) === -1;
                        });
                        if (errors.length === 0) return response;
                        else
                            codes = errors;
                    } else
                        codes = errors;
                } else {
                    if (errors + "" === "0") return response;
                    if (skipErrors != undefined && skipErrors.length > 0 && skipErrors.indexOf(errors) !== -1) return response;
                    codes = [errors];
                }
                return getEjariErrorMessage(...codes).then((message) => {
                    return Promise.reject(message);
                });
            }
        }
    }
    return response;
}



export function checkValidationErrorWithMessage(response, key: string = "Errors"): Promise<any> {
    if (response) {
        if (response.hasOwnProperty(key)) {
            let errors = response[key];
            if (errors != undefined) {
                if (Array.isArray(errors) && errors.length > 0) {
                    let errorMessage = response.Errors.map(error => {
                        return I8N.locale() === "ar" ? error.Message.Ar : error.Message.En;
                    });
                    return Promise.reject(errorMessage);
                }
            }
        }
    }
    return response;
}*/

//will throw error code client should convert to locale string
export function validatePropertyForAction( property: EjariProperty, action: TenancyContactAction ): Promise<EjariProperty> {
	if ( action === TenancyContactAction.RENEW_CONTRACT ) {
		let lastContract = getLastContract( property );
		if ( lastContract == null ) {
			return Promise.reject( "ejari_data_incomplete" );
		} else if ( lastContract.ContractTypeId === 2 ) {
			return Promise.reject( "certificate_contract_renew_error" );
		}
	}
	return Promise.resolve( property );
}

export function validatePropertyFromApi( property: EjariProperty, action: TenancyContactAction, validateReq: ValidatePropertyRequest ) {
	const skip = ( action === TenancyContactAction.RENEW_CONTRACT || action === TenancyContactAction.TERMINATE_CONTRACT ) ? [ 2 ] : [];
	return EjariApi.validateProperty( validateReq, skip );
}


export function setPropertyId( data: EjariProperty, propertyId: number ) {
	data.EjariPropertyId = propertyId;
	switch ( data.PropertyTypeId ) {
		case EjariPropertyType.LAND:
			data.LandEjariPropertyId = "" + propertyId;
			break;
		case EjariPropertyType.BUILDING:
			data.BuildingEjariPropertyId = "" + propertyId;
			break;
		case EjariPropertyType.VILLA:
			data.VillaEjariPropertyId = "" + propertyId;
			break;
		case EjariPropertyType.UNIT:
			data.UnitEjariPropertyId = "" + propertyId;
			break;
		default:
			break;
	}
}

interface LookupConsumer {
	onLookup: ( lookup: EjariLookUp | string, values: Lookup[] ) => void;
}

export interface LookupProcessor {
	( lookups: Lookup[] ): Lookup[];
}

export const AlphaSortProcessor: LookupProcessor = lookups => {
	if ( lookups ) {
		return _.sortBy( lookups, x => {
			let localValue = LocaleUtil.getLocalValue( x );
			if ( localValue ) localValue = localValue.trim();
			return localValue;
		} );
	}
	return lookups;
};
export const LicSourceProcessor: LookupProcessor = lookups => {
	if ( lookups ) {
		const lkp = AlphaSortProcessor( lookups );
		return lkp.filter( x => x.Children && x.Children?.length > 0 );
	}
	return lookups;
};

export function populateLookupOnTo( lookup: EjariLookUp | string, consumer?: LookupConsumer, transformer?: LookupTransformer, processor?: LookupProcessor, callback?: ( values: Lookup[] ) => void ) {
	EjariApi.getLookup( lookup, "", "", transformer )
	.then( lookups => {
		if ( processor )
			lookups = processor( lookups );
		if ( consumer ) {
			consumer.onLookup( lookup, lookups );
		}
	} ).catch( e => {
		console.error( "Loading lookup failed ", lookup, e );
	} );
}

type FilterKeys<S, Check> = {
	[k in keyof S]: S[k] extends Check ? k : never
};
type LookupSink<S> = FilterKeys<S, Lookup[] | undefined>;

type LookupSinkKeys<S> = LookupSink<S>[keyof S];

type LookupState<S> = Pick<S, LookupSinkKeys<S>>;

type StateHolder<State, K extends keyof State, Props extends any> = {
	state: State;
	setState<K extends keyof State>(
		state: ( ( prevState: Readonly<State>, props: Readonly<Props> ) => ( Pick<State, K> | State | null ) ) | ( Pick<State, K> | State | null ),
		callback?: () => void
	): void
};

export function StateConsumer<S>( sink: StateHolder<S, LookupSinkKeys<S>, any>, stateKey: LookupSinkKeys<S> ): LookupConsumer {
	return {
		onLookup: ( lookup, values ) => {
			const update: Partial<S> = {};
			update[ stateKey ] = values as any;
			sink.setState( update as S );
		}
	};
}

export function filterLookupByParentId( all: Lookup[], parentId?: number ) {
	return all.filter( x => x.ParentId === parentId );
}

export function isGovernmentAuthority( authority: Lookup ): boolean {
	if ( authority && authority.en ) {
		return authority.en.toLowerCase() === "government companies";
	}
	return false;
}

export function isLicStateAuthority( authority: Lookup ) {
	if ( authority ) {
		const id = authority.Id;
		return id + "" === 1 + "" || id + "" === 3 + "";
	}
	return false;

}

export function convertToLocalMobile( mobile: string, strict: boolean = false ): string {
	if ( mobile.startsWith( "971" ) )
		return mobile.replace( /^971/, "0" );
	if ( mobile.startsWith( "5" ) )
		return "0" + mobile;
	return mobile;
}

export function getEjariPropertyTypeName( typeId: EjariPropertyType ) {
	switch ( typeId ) {
		case EjariPropertyType.LAND:
			return Strings.land;
		case EjariPropertyType.BUILDING:
			return Strings.building;
		case EjariPropertyType.VILLA:
			return Strings.villa;
		case EjariPropertyType.UNIT:
			return Strings.unit;
		default:
			return "";
	}
}

export function getPropertyId( data: EjariProperty ) {
	switch ( data.PropertyTypeId ) {
		case EjariPropertyType.LAND:
			return data.LandPropertyId;
		case EjariPropertyType.BUILDING:
			return data.BuildingPropertyId;
		case EjariPropertyType.VILLA:
			return data.VillaPropertyId;
		case EjariPropertyType.UNIT:
			return data.UnitPropertyId;
		default:
			return undefined;
	}
}

export function getEjariPropertyId( data: EjariProperty ) {
	switch ( data.PropertyTypeId ) {
		case EjariPropertyType.LAND:
			return data.LandEjariPropertyId;
		case EjariPropertyType.BUILDING:
			return data.BuildingEjariPropertyId;
		case EjariPropertyType.VILLA:
			return data.VillaEjariPropertyId;
		case EjariPropertyType.UNIT:
			return data.UnitEjariPropertyId;
		default:
			return null;
	}
}

export function getTenantRequestStatus( status: TenantRequestStatus ) {
	switch ( status ) {
		case TenantRequestStatus.ACTIVE:
			return Strings.active;
		case TenantRequestStatus.PENDING:
			return Strings.pending_owner;
		case TenantRequestStatus.TERMINATE:
			return Strings.terminated;
		case TenantRequestStatus.PAYMENT_PENDING:
			return Strings.payment_pending;
		case TenantRequestStatus.REQUEST_FOR_UPDATE:
			return Strings.modification_request;
		case TenantRequestStatus.CANCEL:
			return Strings.cancelled;
		case TenantRequestStatus.REJECTED:
			return Strings.rejected;
		case TenantRequestStatus.SUBMIT:
			return Strings.submit;
		default:
			return Strings.pending;
	}
}

export function getTenantRequestStatusClass( statusId?: number ) {
	switch ( statusId ) {
		case TenantRequestStatus.ACTIVE:
			return "completed";
		case TenantRequestStatus.PENDING:
			return "pending";
		case TenantRequestStatus.TERMINATE:
		case TenantRequestStatus.REJECTED:
			return "rejected";
		case TenantRequestStatus.PAYMENT_PENDING:
			return "pending-payment";
		case TenantRequestStatus.REQUEST_FOR_UPDATE:
			return "request-for-update";
		case TenantRequestStatus.CANCEL:
			return "cancelled";
		case TenantRequestStatus.SUBMIT:
			return "submit";
		default:
			return "pending";
	}
}

export function MgmStatusIdToColor( statusId: number ): string {
	switch ( statusId ) {
		case 6:
			return "#637486";
		case 8:
			return "#E42015";
		default:
			return "#637486";
	}
}

export function getSafePropertyIdForDetails( propertyInfo: EjariProperty ): string {
	const idKey = propertyInfo.PropertyIdKey;
	if ( idKey && !isEmptyStringOrNull( idKey ) )
		return idKey + "";
	let propertyId = propertyInfo.PropertyId ?? "";
	return propertyId + "";
}

export function EjariTypeToName( pType: number ) {
	switch ( pType ) {
		case EjariPropertyType.LAND:
			return LVStrings.land;
		case EjariPropertyType.UNIT:
			return LVStrings.unit;
		case EjariPropertyType.BUILDING:
			return LVStrings.building;
		case EjariPropertyType.VILLA:
			return LVStrings.villa;
		case EjariPropertyType.VIRTUAL_VILLA:
			return LVStrings.virtual_unit;
		default:
			return LVStrings.unit;
	}
}

export function normalizeAssociatedProperty( property: AssociatedDetailedProperty ): Partial<EjariProperty> {
	let p: Partial<EjariProperty>;

	const ejariTypeName = EjariTypeToName( property.PropertyTypeId );
	p = {
		IsFreeHold: property.PropertyIsFreeHold === 1,
		PropertyType: IDtoEnum<EjariPropertyType>( EjariPropertyType, property.PropertyTypeId )!,
		PropertyTypeId: property.PropertyTypeId,
		BuildingNameEn: property.BuildingNameEn,
		BuildingNameAr: property.BuildingNameAr,
		BuildingName: {
			en: property.BuildingNameEn,
			ar: property.BuildingNameAr,
		},
		AreaAr: property.LandAreaNameAr,
		AreaEn: property.LandAreaNameEn,
		Area: {
			en: property.LandAreaNameEn,
			ar: property.LandAreaNameAr,
		},
		EjariTypeNameEn: ejariTypeName.en,
		EjariTypeNameAr: ejariTypeName.ar,
		EjariPropertyId: property.PropertyEpropId,
		Usage: {
			en: property.PropertyUsages[ 0 ].NameEn,
			ar: property.PropertyUsages[ 0 ].NameAr,
		},
		EjariUsage: {
			en: property.PropertyUsages[ 0 ].NameEn,
			ar: property.PropertyUsages[ 0 ].NameAr,
		},
		UnitNo: property.UnitNumber,
		PropertyNumber: property.UnitNumber,
		Size: property.PropertyActualArea + "",
		PropertyId: property.PropertyId,
	};
	let owners: any[] = property.Owners;
	if ( owners && owners.length > 0 ) {
		owners = owners.map( value => {
			return {
				...value,
				OwnerName: {
					en: value.OwnerNameEn,
					ar: value.OwnerNameAr,
				},
			};
		} );
		property.Owners = owners;
	}
	return p;
}
